// Tiny SVG waveform thumbnail (~120 x 32 by default). Renders a pre-computed
// peaks array straight to <path>; no canvas, no audio decoding here. Used in
// the take-review queue rows.

export interface WaveformThumbProps {
  peaks: number[] | null
  width?: number
  height?: number
  /** Color of the filled bars; defaults to var(--accent). */
  color?: string
}

export function WaveformThumb({
  peaks,
  width = 120,
  height = 32,
  color = 'var(--accent)',
}: WaveformThumbProps) {
  if (!peaks || peaks.length === 0) {
    return (
      <div
        style={{ width, height }}
        className="rounded-[var(--radius-sm)] bg-[var(--bg-3)]"
        aria-label="No waveform available"
      />
    )
  }

  // Sample down or up to 64 bars. Peaks are absolute amplitudes 0..1.
  const TARGET_BARS = 64
  const step = peaks.length / TARGET_BARS
  const bars: number[] = []
  for (let i = 0; i < TARGET_BARS; i++) {
    const start = Math.floor(i * step)
    const end = Math.min(peaks.length, Math.ceil((i + 1) * step))
    let max = 0
    for (let j = start; j < end; j++) {
      const v = Math.abs(peaks[j] ?? 0)
      if (v > max) max = v
    }
    bars.push(max)
  }

  const barWidth = width / TARGET_BARS
  const halfH = height / 2

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label="Take waveform thumbnail"
      style={{ display: 'block' }}
    >
      {bars.map((amp, i) => {
        const h = Math.max(1, amp * height * 0.9)
        return (
          <rect
            key={i}
            x={i * barWidth}
            y={halfH - h / 2}
            width={Math.max(1, barWidth - 1)}
            height={h}
            fill={color}
            rx={1}
          />
        )
      })}
    </svg>
  )
}
